import * as Auth from '../../services/auth';
import * as Authenticate from '../../services/auth.authenticate';
import * as AuthUtils from '../../utils/auth.utils';
import { clearAuthorization } from '../../wrapper/auth.request.configurator';

import {
  AUTH_AUTHENTICATE_SUCCESS,
  AUTH_CLEAR_USER_INFO,
  AUTH_ERROR,
  AUTH_FORGOT_PASSWORD,
  AUTH_FETCH_BY_TOKEN,
  AUTH_FETCHED_BY_TOKEN,
  AUTH_RESET_PASSWORD,
  AUTH_SIGN_IN,
  AUTH_SIGN_UP,
  AUTH_SIGN_OUT,
  AUTH_UPDATE_PASSWORD,
  AUTH_USER_CONFIRMATION_REQUEST_ERROR,
  AUTH_USER_CONFIRMATION_REQUEST_SUCCESS,
  AUTH_USER_CONFIRMATION_TOKEN_EXPIRED,
  AUTH_USER_CONFIRMATION_TOKEN_VALIDATED,
  AUTH_LINK_GOOGLE_REQUEST_SUCCESS,
  AUTH_LINK_GOOGLE_REQUEST_ERROR,
  AUTH_LINK_FACEBOOK_REQUEST_SUCCESS,
  AUTH_LINK_FACEBOOK_REQUEST_ERROR,
  AUTH_UNLINK_GOOGLE_REQUEST_SUCCESS,
  AUTH_UNLINK_GOOGLE_REQUEST_ERROR,
  AUTH_UNLINK_FACEBOOK_REQUEST_SUCCESS,
  AUTH_UNLINK_FACEBOOK_REQUEST_ERROR,
  AUTH_USER_ACTIVATION_REQUEST_ERROR,
  AUTH_USER_ACTIVATION_REQUEST_SUCCESS,
} from './types';

const useActions = (dispatch) => {
  /**
    * Handle auth response errors.
    * Trigger a dispatch AUTH_ERROR to reducers.
  */
 const handleResponseError = (error) => {
    if (error && error.data && error.data.invalid_session) {
      AuthUtils.removeUserToken();
      clearAuthorization();
    }
    dispatch({ type: AUTH_ERROR, payload: { error } });
  }

  return ({
    signIn: async (data) => {
      try {
        let response;

        const {
          provider, code, clientId, redirectUri, email, password, uuid,
        } = data;

        if (provider) {
          response = await Authenticate.signIn(provider, {
            code,
            clientId,
            redirectUri,
            uuid,
          });
        } else {
          response = await Authenticate.signIn('mcontigo', {
            email,
            password,
            uuid,
          });
        }

        dispatch({ type: AUTH_SIGN_IN, payload: response });
        dispatch({ type: AUTH_AUTHENTICATE_SUCCESS, payload: response });
      } catch (error) {
        handleResponseError(error);
      }
    },
    signUp: async (data) => {
      try {
        let response;

        const {
          // eslint-disable-next-line camelcase
          provider, code, clientId, redirectUri, email, password, displayName, uuid, user_info, gender,
        } = data;

        if (provider) {
          response = await Authenticate.signUp(provider, {
            code,
            clientId,
            redirectUri,
            uuid,
            user_info,
            gender,
          });
        } else {
          response = await Authenticate.signUp('mcontigo', {
            email,
            password,
            displayName,
            uuid,
            user_info,
            gender,
          });
        }

        dispatch({ type: AUTH_SIGN_UP, payload: response });
        dispatch({ type: AUTH_AUTHENTICATE_SUCCESS, payload: response });
      } catch (error) {
        handleResponseError(error);
      }
    },
    signOut: async () => {
      try {
        await Authenticate.signOut();
        dispatch({ type: AUTH_SIGN_OUT });
        dispatch({ type: AUTH_CLEAR_USER_INFO });
      } catch (error) {
        handleResponseError(error);
      }
    },
    fetchUserByToken: async () => {
      try {
        const user = await Auth.getCurrentUser();
        dispatch({ type: AUTH_FETCH_BY_TOKEN, payload: { user } });
        if (user) dispatch({ type: AUTH_FETCHED_BY_TOKEN, payload: { user } });
      } catch (error) {
        handleResponseError(error);
      }
    },
    forgotPassword: async ({ email }) => {
      try {
        await Auth.forgotUserPassword({ email });
        dispatch({ type: AUTH_FORGOT_PASSWORD, payload: { emailSent: true } });
      } catch (error) {
        handleResponseError(error);
      }
    },
    resetPassword: async (token) => {
      try {
        await Auth.resetUserPassword({ token });
        dispatch({ type: AUTH_RESET_PASSWORD, payload: { isValidToken: true } });
      } catch (error) {
        handleResponseError(error);
      }
    },
    changeUserPassword: async ({ token, newPassword, confirmPassword }) => {
      try {
        await Auth.changeUserPassword({ token, newPassword, confirmPassword });
        dispatch({ type: AUTH_UPDATE_PASSWORD, payload: { completed: true } });
      } catch (error) {
        handleResponseError(error);
      }
    },
    validateUserAccountConfirmation: async (token) => {
      try {
        await Auth.validateUserConfirmationToken({ token });
        dispatch({
          type: AUTH_USER_CONFIRMATION_TOKEN_VALIDATED,
          payload: { isValidToken: true, waitingTokenValidate: false },
        });
      } catch (error) {
        dispatch({
          type: AUTH_USER_CONFIRMATION_TOKEN_EXPIRED,
          payload: { isValidToken: false, waitingTokenValidate: false },
        });
        handleResponseError(error);
      }
    },
    requestUserAccountConfirmation: async (email) => {
      try {
        await Auth.requestUserAccountConfirmation({ email });
        dispatch({ type: AUTH_USER_CONFIRMATION_REQUEST_SUCCESS, payload: { emailSent: true } });
      } catch (error) {
        dispatch({ type: AUTH_USER_CONFIRMATION_REQUEST_ERROR, payload: { emailSent: false } });
        handleResponseError(error);
      }
    },
    requestLinkAccountGoogle: async (data) => {
      try {
        const response = await Auth.linkAccountProvider({ ...data, provider: 'google' });
        dispatch({ type: AUTH_LINK_GOOGLE_REQUEST_SUCCESS, payload: response });
      } catch (error) {
        dispatch({ type: AUTH_LINK_GOOGLE_REQUEST_ERROR, payload: {} });
        handleResponseError(error);
      }
    },
    requestLinkAccountFacebook: async (data) => {
      try {
        const response = await Auth.linkAccountProvider({ ...data, provider: 'facebook' });
        dispatch({ type: AUTH_LINK_FACEBOOK_REQUEST_SUCCESS, payload: response });
      } catch (error) {
        dispatch({ type: AUTH_LINK_FACEBOOK_REQUEST_ERROR, payload: {} });
        handleResponseError(error);
      }
    },
    requestUnlinkAccountGoogle: async () => {
      try {
        const response = await Auth.unlinkAccountProvider({ provider: 'google' });
        dispatch({ type: AUTH_UNLINK_GOOGLE_REQUEST_SUCCESS, payload: response });
      } catch (error) {
        dispatch({ type: AUTH_UNLINK_GOOGLE_REQUEST_ERROR, payload: {} });
        handleResponseError(error);
      }
    },
    requestUnlinkAccountFacebook: async () => {
      try {
        const response = await Auth.unlinkAccountProvider({ provider: 'facebook' });
        dispatch({ type: AUTH_UNLINK_FACEBOOK_REQUEST_SUCCESS, payload: response });
      } catch (error) {
        dispatch({ type: AUTH_UNLINK_FACEBOOK_REQUEST_ERROR, payload: {} });
        handleResponseError(error);
      }
    },
    activateAccount: async (userId, data) => {
      try {
        const response = await Authenticate.activate(userId, data);
        dispatch({ type: AUTH_USER_ACTIVATION_REQUEST_SUCCESS, payload: response });
      } catch (error) {
        dispatch({ type: AUTH_USER_ACTIVATION_REQUEST_ERROR, payload: {} });
        handleResponseError(error);
      }
    }
  })
}

export { useActions };
